import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { findMobileOS } from 'yoda-core-components/lib/helpers/Utils/Utils';
import Icon from 'yoda-core-components/lib/components/Icon/next';
import Button from 'yoda-core-components/lib/components/Button';
import PropTypes, { bool } from 'prop-types';
import Video from '../VideoPlayer/VideoPlayer';
import IconTriangle from '../../assets/svg/Triangle.svg';
import { getDeviceType as getDeviceTypeSelector } from '../../selectors/CMPageSelector';
import { commonAutomationIds, ctaAutomationIds } from '../CoreMediaPage/CMPage.config';

import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';

const StoryVideo = (props) => {
    const getVideoProvider = (videoProvider) =>
        (videoProvider && videoProvider.toUpperCase()) || '';
    const data = get(props, 'data', props);
    const { automationId, componentType, rndKeyString, storyVideo } = data;
    const bgColor = get(data, 'styles.componentBackgroundFillColor', '');
    const isBgColorAvailable = bgColor !== 'ffffff';
    const containerColor = `#${bgColor}`;
    const styles = get(data, 'styles', {});
    const teaserTitle = get(data, 'teaserTitle', '');
    const teaserSubtitle = get(data, 'teaserSubtitle', '');
    const messageGraphicAvailable = get(data, 'messageGraphic', {});
    const legalTextData = get(data, 'legalText', {});
    const ctaData = get(data, 'ctaLinks', []);
    const isVideoPlacementOnTop = styles?.componentVideoThumbnailPlacement === 'Top';
    const isVideoEdgeToEdge = componentType === 'JCPStoryVideoEdgeToEdge';
    const isHeadlineOnTop = styles?.componentHeadlinePosition === 'Above';
    const { isCMPreview, isLHN } = props;
    const [ctaDisplay, setCtaDisplay] = useState(false);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const deviceType = useSelector((state) => getDeviceTypeSelector(state));

    const storyVideoRef = useRef(null);

    const componetStylingResp = {
        padding: {
            S: `${dt(['m-6'])}`,
            M: `${dt(['m-12'])}`,
            L: `${dt(['m-20'])}`,
            None: `${dt(['m-0'])}`,
        },
        fontSize: {
            'heading-1': `${dt(['heading-1'])}`,
            'heading-2': `${dt(['heading-2'])}`,
            'heading-3': `${dt(['heading-3', 'lg:leading-[60px]'])}`,
            'heading-4': `${dt(['heading-4'])}`,
            'heading-5': `${dt(['heading-5'])}`,
            'heading-6': `${dt(['heading-6'])}`,
        },
        HeadLinePosition: {
            Above: `${dt(['md:mt-6', 'smOnly:mt-4', 'text-center'])}`,
        },
        subTitleFontSize: {
            'body-1': `${dt(['text-normal'])}`,
            'body-2': `${dt(['text-xlarge'])}`,
        },
        MessageLockupAlignment: {
            Left: `${dt(['items-start'])}`,
            Center: `${dt(['items-end', 'items-center', 'text-center', 'self-center'])}`,
            Right: `${dt(['justify-end', 'items-end', 'text-right'])}`,
        },
        VideoThumbnailPlacement: {
            Left: `${dt(['order-0'])}`,
            Center: `${dt(['order-0'])}`,
            Right: `${dt(['md:order-1', 'sm:order-0', 'lg:order-1'])}`,
        },
        CtaStyle: {
            Left: `${dt(['justify-start'])}`,
            Center: `${dt(['justify-center'])}`,
            Right: `${dt(['justify-end'])}`,
        },
    };

    const optionsForIntersectionObserver = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
    };

    const autoPlayYoutubeVideoinViewPort = (youTubeVideo) => {
        youTubeVideo.mute();
        youTubeVideo.playVideo();
    };

    const autoPlayScene7VideoinViewPort = (scene7VideoDOM) => {
        scene7VideoDOM.muted = true;
        scene7VideoDOM.loop = true;
        scene7VideoDOM.play();
    };

    /* istanbul ignore next */
    const callbackFunctionForObserver = (entries) => {
        const [entry] = entries;
        const { videoAutoplay, videoProvider, value } = storyVideo;
        const videoDOM = videoProvider === 'Scene7' && entry.target.querySelector('video');
        let youTubeVideo = null;
        if (videoProvider === 'Youtube' && window.YT) {
            if (rndKeyString) {
                youTubeVideo = window.YT.get(`${value}-${rndKeyString}`);
            } else {
                youTubeVideo = window.YT.get(`${value}-`);
            }
        }
        if (entry.isIntersecting && videoAutoplay && isVideoEdgeToEdge) {
            if (videoProvider === 'Youtube') {
                youTubeVideo && autoPlayYoutubeVideoinViewPort(youTubeVideo);
            } else {
                videoDOM && autoPlayScene7VideoinViewPort(videoDOM);
                if (findMobileOS() !== 'iOS') {
                    const mutableVolumeElement = document.getElementById(
                        `scene7-${value}-${rndKeyString}_mutableVolume`
                    );
                    const scene7VideoVolumeElement =
                        mutableVolumeElement && mutableVolumeElement.childNodes[0];
                    scene7VideoVolumeElement &&
                        scene7VideoVolumeElement.s7base &&
                        scene7VideoVolumeElement.s7base.setSelected(true);
                }
            }
        } else if (videoProvider === 'Youtube') {
            youTubeVideo && youTubeVideo.pauseVideo();
        } else {
            videoDOM && videoDOM.pause();
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            callbackFunctionForObserver,
            optionsForIntersectionObserver
        );
        observer.POLL_INTERVAL = 100;
        observer.USE_MUTATION_OBSERVER = false;
        storyVideoRef.current && observer.observe(storyVideoRef.current);

        return () => {
            storyVideoRef.current && observer.unobserve(storyVideoRef.current);
        };
    }, [storyVideoRef, optionsForIntersectionObserver, isVideoLoaded]);

    const MessageLockUpAlignment =
        componetStylingResp?.MessageLockupAlignment[styles?.componentMessageLockupAlignment];
    const componentHeadlineFontSize =
        componetStylingResp?.fontSize[styles?.componentHeadlineFontSize];
    const componentHeadlinePosition =
        componetStylingResp?.HeadLinePosition[styles?.componentHeadlinePosition];
    const componentSubHeadlineFontSize =
        componetStylingResp?.subTitleFontSize[styles?.componentSubHeadlineFontSize];
    const VideoThumbnailPlacement =
        componetStylingResp?.VideoThumbnailPlacement[styles.componentVideoThumbnailPlacement];
    const ComponentCtaStyle =
        componetStylingResp?.CtaStyle[styles?.componentMessageLockupAlignment];

    const MessageGraphic = () => {
        const { data, description } = messageGraphicAvailable;
        const messageGraphicSrc = `${data?.uri}`;

        return (
            <div
                className={dt([
                    'mb-2',
                    'flex',
                    'justify-center',
                    'lg:max-w-[254px]',
                    'lg:max-h-16',
                    'smMd:max-w-[160px]',
                    'smMd:max-h-[40px]',
                ])}
                data-cm-metadata={isCMPreview && metaDataElement(props?.data, 'pictures')}
                data-automation-id={commonAutomationIds?.MESSAGEGRAPHIC}
            >
                <img
                    src={messageGraphicSrc}
                    alt={description}
                    title={description}
                    data-cm-metadata={isCMPreview && metaDataProperty('data')}
                    className={dt(['w-auto', 'lg:h-16', 'smMd:h-10'])}
                />
            </div>
        );
    };

    const TeaserTitle = () => {
        return (
            <div
                className={`${componentHeadlineFontSize} ${componentHeadlinePosition} ${dt([
                    'font-open-sans-bold',
                    'text-black',
                    '-tracking-[2px]',
                    'break-words',
                    isHeadlineOnTop ? 'mb-0' : 'mb-2',
                ])}`}
                style={{ color: `#${styles?.componentHeadlineColor}`, inlineSize: '100%' }}
                data-cm-metadata={isCMPreview && metaDataProperty('teaserTitle')}
                data-automation-id={commonAutomationIds?.MAINTITLE}
            >
                {teaserTitle}
            </div>
        );
    };

    const TeaserSubtitle = () => {
        return (
            <div
                className={`${componentSubHeadlineFontSize} ${dt(['mb-2', 'text-black'])}`}
                style={{ color: `#${styles?.componentSubHeadlineColor}` }}
                data-cm-metadata={isCMPreview && metaDataProperty('teaserSubtitle')}
                data-automation-id={commonAutomationIds?.MAINSUBTITLE}
            >
                {teaserSubtitle}
            </div>
        );
    };

    const LegalText = () => {
        const { value, openInNewTab } = legalTextData?.urlHolder;
        const linkTragetValue = openInNewTab ? '_blank' : '';
        return (
            <div
                className={dt(['text-xsmall', 'mb-2'])}
                data-cm-metadata={isCMPreview && metaDataProperty('legalText')}
                data-automation-id={commonAutomationIds?.LEGALTEXT}
            >
                <span
                    style={{ color: `#${styles?.legalTextColor}` }}
                    id="legalText"
                    data-cm-metadata={isCMPreview && metaDataProperty('legalText.legalText')}
                >
                    {legalTextData?.legalText}
                    &nbsp;{' '}
                    <a
                        className={dt(['underline', 'text-gray-70'])}
                        aria-label={legalTextData?.legalLinkText}
                        target={linkTragetValue}
                        href={value}
                        alt={legalTextData?.legalAltText}
                        title={legalTextData?.legalAltText}
                        data-automation-id={commonAutomationIds?.LEGALLINK}
                        rel="noreferrer noopener"
                        style={{ color: `#${styles?.legalLinkColor}` }}
                        data-cm-metadata={
                            isCMPreview && metaDataProperty('legalText.legalLinkText')
                        }
                    >
                        {legalTextData?.legalLinkText}
                    </a>
                </span>
            </div>
        );
    };

    const componentCTALinkStyle = dt(['flex', 'w-auto', 'items-center', 'justify-center']);
    const componentCTABtnStyle = dt([
        'flex',
        'md:w-60',
        'smOnly:w-full',
        'items-center',
        'justify-center',
        'gap-1',
    ]);

    const CtaComponet = () => {
        const isLink = styles?.componentCtaStyle === 'link';
        const isDropDown = styles?.componentCtaStyle === 'button' && ctaData?.length > 2;
        const isButton = styles?.componentCtaStyle !== 'link' && ctaData?.length <= 2;
        const dropbtnStyle = dt([
            'flex',
            'justify-center',
            'items-center',
            'bg-white',
            'text-black',
            'text-xLg',
            'border',
            'border-solid',
            'border-gray-40',
            'cursor-pointer',
            'md:w-60',
            'sm:w-full',
            'py-2',
            'px-6',
            'rounded-lg',
            'whitespace-nowrap',
            'gap-1',
        ]);
        const dropdownContentStyle = dt([
            !ctaDisplay && 'hidden',
            'absolute',
            'bg-white',
            'md:w-60',
            'shadow-elevation1',
            'sm:w-full',
            'text-center',
            ctaDisplay && 'block',
            'z-[3]',
        ]);
        const dropdownContentStylehover = dt([
            'hidden',
            'absolute',
            'bg-white',
            'md:w-60',
            'shadow-elevation1',
            'sm:w-full',
            'text-center',
            'group-hover:block',
            'z-[3]',
        ]);
        const toggleButton = () => {
            setCtaDisplay(!ctaDisplay);
        };
        const linkWithoutTitleEmpty =
            styles?.componentCtaStyle !== '' &&
            isEmpty(messageGraphicAvailable) &&
            !teaserTitle &&
            !teaserSubtitle &&
            isEmpty(legalTextData?.legalLinkText) &&
            isEmpty(legalTextData?.legalText)
                ? 'smMd:mt-0'
                : 'smMd:mt-4';
        return (
            <div
                className={dt([
                    'flex',
                    'flex-wrap',
                    'smOnly:w-full',
                    'gap-x-4',
                    styles?.componentCtaStyle === 'link' && 'gap-y-4',
                    styles?.componentCtaStyle === 'link' && 'mt-4',
                    styles?.componentCtaStyle === 'link' && 'lg:mt-2',
                    styles?.componentCtaStyle !== 'link' && 'mt-4',
                    linkWithoutTitleEmpty,
                    `${ComponentCtaStyle}`,
                    isVideoPlacementOnTop && 'mdOnly:justify-center',
                    isVideoPlacementOnTop && 'lg:justify-center',
                    isButton && 'gap-4',
                    !isVideoPlacementOnTop && isButton && 'w-full',
                ])}
            >
                {isLink &&
                    ctaData?.map((currentCta) => (
                        <>
                            <Button
                                className={componentCTALinkStyle}
                                id="componentCTA"
                                aria-label={currentCta?.ctaText}
                                target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                linkUrl={currentCta?.urlHolder?.value}
                                alt={currentCta?.ctaAltText}
                                title={currentCta?.ctaAltText}
                                buttonType="Text"
                                size="Lg"
                                type="button"
                                automationId={ctaAutomationIds?.LINK}
                                style={{ color: `#${styles?.componentCtaColor}` }}
                                data-cm-metadata={
                                    isCMPreview && metaDataProperty('ctaLinks.ctaText')
                                }
                            >
                                {currentCta?.ctaText}
                            </Button>
                        </>
                    ))}
                {isDropDown && (
                    <>
                        <div
                            className={dt(['relative', 'inline-block', 'group', 'sm:basis-full'])}
                            data-automation-id={ctaAutomationIds?.DROPDOWN}
                        >
                            <button
                                className={dropbtnStyle}
                                id="componentCTA_dropdown"
                                onClick={deviceType.isDesktop !== true && toggleButton}
                                data-automation-id={ctaAutomationIds?.DROPDOWNBTN}
                            >
                                <span className={dt(['font-semibold'])}>Shop All</span>
                                <Icon
                                    iconType="svg"
                                    className={dt(['ml-2'])}
                                    width="16px"
                                    height="16px"
                                    viewBox="0 0 16 16"
                                >
                                    <IconTriangle />
                                </Icon>
                            </button>
                            <div
                                data-automation-id={ctaAutomationIds?.DROPDOWNITEMS}
                                className={
                                    deviceType.isDesktop !== true
                                        ? `${dropdownContentStyle}`
                                        : `${dropdownContentStylehover}`
                                }
                            >
                                {ctaData?.map((currentCta) => (
                                    <a
                                        aria-label={currentCta?.ctaText}
                                        target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                        href={currentCta?.urlHolder?.value}
                                        alt={currentCta?.ctaAltText}
                                        title={currentCta?.ctaAltText}
                                        data-automation-id={ctaAutomationIds?.DROPDOWNLINK}
                                        rel="noreferrer noopener"
                                        className={dt([
                                            'text-black',
                                            'py-2.5',
                                            'px-6',
                                            'no-underline',
                                            'block',
                                            'hover:bg-light-white',
                                        ])}
                                        data-cm-metadata={
                                            isCMPreview && metaDataProperty('ctaLinks.ctaText')
                                        }
                                    >
                                        {currentCta?.ctaText}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </>
                )}
                {isButton &&
                    ctaData?.map((currentCta) => (
                        <>
                            <Button
                                className={componentCTABtnStyle}
                                aria-label={currentCta?.ctaText}
                                target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                linkUrl={currentCta?.urlHolder?.value}
                                alt={currentCta?.ctaAltText}
                                title={currentCta?.ctaAltText}
                                id="componentCTA_button"
                                automationId={ctaAutomationIds?.BUTTON}
                                buttonType="Secondary"
                                size="Lg"
                                type="button"
                                data-cm-metadata={
                                    isCMPreview && metaDataProperty('ctaLinks.ctaText')
                                }
                            >
                                {currentCta?.ctaText}
                            </Button>
                        </>
                    ))}
            </div>
        );
    };

    const messageLockupStyleVideoOnTop = !isVideoPlacementOnTop
        ? dt([
              'w-full',
              !isVideoEdgeToEdge && 'lg:w-[calc(41.666667%-84px)]',
              isBgColorAvailable && 'lg:w-[calc(41.666667%-80px)]',
              !isVideoEdgeToEdge && 'mdOnly:w-[calc(41.666667%-36px)]',
              'justify-center',
          ])
        : '';
    const messageLockupStyleFullVideo = isVideoEdgeToEdge
        ? dt(['lg:w-[calc(66.666667%-6px)]', 'mdOnly:w-full', 'mx-auto'])
        : '';
    const messageLockupText = () => (
        <div
            className={dt([
                'smOnly:w-full',
                'flex',
                'flex-col',
                'smOnly:px-6',
                isVideoPlacementOnTop && 'mdOnly:w-[calc(58.333333%+12px)]',
                MessageLockUpAlignment,
                isVideoPlacementOnTop &&
                    !isVideoEdgeToEdge &&
                    !isBgColorAvailable &&
                    'lg:w-[calc(58.333333%+20px)]',
                isVideoPlacementOnTop &&
                    !isVideoEdgeToEdge &&
                    isBgColorAvailable &&
                    'lg:w-[calc(58.333333%+48px)]',
                styles?.componentVideoThumbnailPlacement === 'Right' &&
                    isBgColorAvailable &&
                    'lg:ml-4',
                styles?.componentVideoThumbnailPlacement === 'Left' &&
                    isBgColorAvailable &&
                    'lg:mr-4',
                styles?.componentVideoThumbnailPlacement === 'Right' &&
                    !isBgColorAvailable &&
                    'lg:ml-10',
                styles?.componentVideoThumbnailPlacement === 'Left' &&
                    !isBgColorAvailable &&
                    'lg:mr-10',
                messageLockupStyleVideoOnTop,
                messageLockupStyleFullVideo,
            ])}
            data-automation-id={commonAutomationIds?.MESSAGECONTAINER}
            style={{}}
            data-cm-metadata={isCMPreview && metaDataElement(data)}
        >
            {!isEmpty(messageGraphicAvailable) && <MessageGraphic />}
            {!!teaserTitle && !isHeadlineOnTop && <TeaserTitle />}
            {!!teaserSubtitle && <TeaserSubtitle />}
            {(!isEmpty(legalTextData?.legalLinkText) || !isEmpty(legalTextData?.legalText)) && (
                <LegalText />
            )}
            {!isEmpty(ctaData) && <CtaComponet />}
        </div>
    );
    const renderVideo = () => {
        const { value, videoAutoplay = false } = storyVideo;
        return (
            <Video
                provider={getVideoProvider(storyVideo?.videoProvider)}
                playerProps={{ url: value, ...storyVideo }}
                playerId={rndKeyString}
                componentType={componentType}
                disableAutoPlay
                deviceType={deviceType}
                setVideoLoaded={setIsVideoLoaded}
                loop={videoAutoplay && isVideoEdgeToEdge}
                // videoAutoplay={videoAutoplay}
                videoTheme={dt([
                    'sm:w-full',
                    'md:self-center',
                    isVideoEdgeToEdge && 'w-full',
                    !isVideoPlacementOnTop && 'lg:w-full',
                    !isVideoPlacementOnTop && 'lg:mx-0',
                    !isVideoPlacementOnTop && 'lg:pl-0',
                    'sm:!absolute',
                    'top-0',
                    'left-0',
                    'h-full',
                ])}
                automationId={`at-${automationId}-video`}
            />
        );
    };
    const edgeToedgeVideoContainer = isVideoEdgeToEdge
        ? dt(['smOnly:w-full', !isLHN ? 'md:w-[calc(100%+48px)]' : '', !isLHN ? 'md:-mx-6' : ''])
        : dt([
              isVideoPlacementOnTop && !isBgColorAvailable && 'lg:w-[calc(58.333333%+20px)]',
              isVideoPlacementOnTop && isBgColorAvailable && 'lg:w-[calc(58.333333%+48px)]',
              !isVideoPlacementOnTop && !isVideoEdgeToEdge && 'lg:w-[calc(58.333333%+48px)]',
              !isVideoPlacementOnTop && isBgColorAvailable && 'lg:w-[calc(58.333333%+24px)]',
              'mdOnly:w-[calc(58.333333%+12px)]',
              'smOnly:w-full',
              'self-center',
          ]);
    const videoContent = () => {
        const { videoAltText = '' } = storyVideo;
        return (
            <div
                className={dt([
                    'justify-center',
                    isVideoPlacementOnTop && 'smOnly:w-full',
                    edgeToedgeVideoContainer,
                    `${VideoThumbnailPlacement}`,
                ])}
                title={videoAltText}
                data-automation-id={`at-${automationId}-videoContainer`}
            >
                {' '}
                {renderVideo()}
            </div>
        );
    };

    return (
        <div
            style={{ backgroundColor: containerColor }}
            data-automation-id={automationId}
            className={dt([
                'flex',
                'flex-row',
                (isVideoPlacementOnTop || isVideoEdgeToEdge) && 'flex-col',
                'smOnly:flex-col',
                'lg:gap-10',
                'mdOnly:gap-6',
                'smOnly:gap-4',
                'smOnly:-mx-6',
                'smOnly:p-0',
                isBgColorAvailable && 'smMd:w-[calc(100%+48px)]',
                isBgColorAvailable && isVideoEdgeToEdge && 'lg:w-[calc(100%+48px)]',
                isBgColorAvailable && isVideoEdgeToEdge && 'lg:-mx-6',
                isBgColorAvailable && 'smMd:-mx-6',
                isVideoPlacementOnTop && 'm-auto',
                isVideoPlacementOnTop && 'items-center',
                isBgColorAvailable && 'p-6',
                isBgColorAvailable && 'smOnly:pb-6',
                isBgColorAvailable && isVideoEdgeToEdge && 'pt-0',
            ])}
            data-cm-metadata={isCMPreview && metaDataElement(data)}
            ref={storyVideoRef}
        >
            {isHeadlineOnTop && <TeaserTitle />}
            {videoContent()}
            {messageLockupText()}
        </div>
    );
};
StoryVideo.propTypes = {
    rndKeyString: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isCMPreview: bool,
};
StoryVideo.defaultProps = {
    rndKeyString: 'comp_JCPStoryVideo_1',
    isCMPreview: false,
};

export default StoryVideo;
